import { useGetCircuitsUsingBenefit } from '@/hooks/useBenefits';
import Loader from '../ui/Loader';
import { MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import StyledButton from '../styled/StyledButton';
import StyledTable from '../styled/StyledTable';
import {
  CIRCUITS_USED_BY_BENEFIT_COLUMN_DEF,
  HOTELS_USED_BY_BENEFIT_COLUMN_DEF,
} from '@/types/benefits/circuitsUsedByBenefitsColumnDef';
import { PATHS } from '@/constants/Paths';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface ListCircuitsUsingBenefitModalProps {
  closeModal: () => void;
  benefitId: string;
}

const ListCircuitsUsingBenefitModal = ({
  closeModal,
  benefitId,
}: ListCircuitsUsingBenefitModalProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const navigate = useNavigate();

  const handleDialogClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const {
    data: usageData,
    isLoading,
    isSuccess,
  } = useGetCircuitsUsingBenefit(benefitId);

  const circuits = usageData?.circuits ?? [];
  const hotels = usageData?.hotels ?? [];

  const totalCircuits = circuits.length;
  const totalHotels = hotels.length;

  const navigateToCircuit = (circuitId: string) =>
    navigate(PATHS.home.circuits.view.replace(':circuitId', circuitId));

  const navigateToHotel = (benefitId: string) =>
    navigate(PATHS.home.benefits.hotel.view.replace(':benefitId', benefitId));

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && usageData != null && (
      <dialog
        open
        onClick={handleDialogClick}
        className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-black/50"
      >
        <div className="flex flex-col justify-between min-w-96 gap-8 p-6 border rounded-md border-gray-200 bg-white">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <h1 className="text-lg leading-6 font-semibold text-gray-950">
                {t('benefits.circuits.list')}
              </h1>
            </div>
            <p className="text-sm leading-6 text-gray-800 max-w-96">
              {t('benefits.circuits.subtitle', {
                count: totalCircuits,
              })}
            </p>
            <div className="w-full max-h-[700px] overflow-y-auto">
              <StyledTable
                columns={getTranslatedConst(
                  CIRCUITS_USED_BY_BENEFIT_COLUMN_DEF,
                )}
                data={circuits}
                enablePagination={false}
                getRowId={(row) => row.id}
                onRowClick={(row) => navigateToCircuit(row.original.id)}
                onViewRow={(row) => navigateToCircuit(row.original.id)}
              />
            </div>
            {totalHotels > 0 && (
              <>
                <h2 className="text-base font-semibold text-gray-950">
                  {t('benefits.hotels.list')}
                </h2>
                <p className="text-sm leading-6 text-gray-800 max-w-96">
                  {t('benefits.hotels.subtitle', { count: totalHotels })}
                </p>
                <div className="w-full max-h-[400px] overflow-y-auto">
                  <StyledTable
                    columns={getTranslatedConst(
                      HOTELS_USED_BY_BENEFIT_COLUMN_DEF,
                    )}
                    data={hotels}
                    enablePagination={false}
                    getRowId={(row) => row.id}
                    onRowClick={(row) =>
                      navigateToHotel(row.original.benefitId)
                    }
                    onViewRow={(row) => navigateToHotel(row.original.benefitId)}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex gap-3 w-full">
            <StyledButton
              variant="outlined"
              title={t('common.close')}
              className="text-gray-800 border-gray-300 w-full"
              onClick={closeModal}
            />
          </div>
        </div>
      </dialog>
    )
  );
};

export default ListCircuitsUsingBenefitModal;
